import { createContext, ReactNode, useContext, useEffect } from "react";
import {
  useQuery,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { apiRequest, queryClient } from "../lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { setSentryUser, clearSentryUser } from "../lib/sentry";

// Benutzertyp definieren
export interface User {
  id: number;
  email: string;
  roles: string[];
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  avatarUrl?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

// Anmeldedaten
export type LoginData = {
  username: string;
  password: string;
  rememberMe?: boolean;
};

// Registrierungsdaten
export type RegisterData = {
  email: string;
  username: string;
  password: string;
  firstName?: string;
  lastName?: string;
  captchaToken?: string;
};

// Auth-Kontext-Typ
type AuthContextType = {
  user: User | null;
  isLoading: boolean;
  error: Error | null;
  loginMutation: UseMutationResult<User, Error, LoginData>;
  logoutMutation: UseMutationResult<void, Error, void>;
  registerMutation: UseMutationResult<User, Error, RegisterData>;
};

// Auth-Kontext erstellen
export const AuthContext = createContext<AuthContextType | null>(null);

// Auth-Provider-Komponente
export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  
  // Sofort versuchen, Benutzer aus dem localStorage zu laden, falls vorhanden
  // ABER: Nach OAuth-Redirect muss der Cache ignoriert werden
  const loadUserFromStorage = () => {
    try {
      // Check if we're coming from an OAuth redirect — if so, discard cached user
      const params = new URLSearchParams(window.location.search);
      if (params.has('oauth')) {
        localStorage.removeItem('speiseguru_user');
        localStorage.removeItem('speiseguru_user_id');
        return undefined; // undefined = no initial data → forces fetch
      }
      const storedUser = localStorage.getItem('speiseguru_user');
      if (storedUser) {
        return JSON.parse(storedUser);
      }
    } catch (e) {
      console.error("Fehler beim Laden des gespeicherten Benutzers:", e);
    }
    return undefined; // undefined = no initial data → forces fetch
  };
  
  const cachedUser = loadUserFromStorage();
  
  // Aktuellen Benutzer abrufen
  const {
    data: user,
    error,
    isLoading,
  } = useQuery<User | null, Error>({
    queryKey: ["/api/auth/me"],
    queryFn: async () => {
      try {
        const res = await apiRequest("GET", "/api/auth/me");

        if (res.ok) {
          const userData = await res.json();
          try {
            localStorage.setItem('speiseguru_user', JSON.stringify(userData));
            localStorage.setItem('speiseguru_user_id', userData.id.toString());
            localStorage.setItem('speiseguru_last_activity', Date.now().toString());
          } catch (e) {
            console.error("Fehler beim Speichern des Benutzers in localStorage:", e);
          }
          return userData;
        }

        if (res.status === 401) {
          // Session abgelaufen — localStorage-Cache bereinigen
          try {
            localStorage.removeItem('speiseguru_user');
            localStorage.removeItem('speiseguru_user_id');
          } catch (e) {
            // ignore
          }
          return null;
        }

        throw new Error("Fehler beim Abrufen des Benutzers");
      } catch (error) {
        console.error("Auth-Fehler:", error);
        return null;
      }
    },
    initialData: cachedUser, // undefined = no cache → will fetch immediately
    staleTime: cachedUser ? 5 * 60 * 1000 : 0, // 5 min cache if from localStorage; 0 (always stale) if no cache
    gcTime: 60 * 60 * 1000, // 1 Stunde garbage collection time
    refetchOnWindowFocus: true,
    refetchOnMount: 'always', // ALWAYS refetch on mount to catch OAuth redirects
    refetchOnReconnect: true,
    retry: 3
  });

  // Login-Mutation
  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginData) => {
      const res = await apiRequest("POST", "/api/auth/login", credentials);
      
      if (!res.ok) {
        try {
          const data = await res.json();
          throw new Error(data.message || "Anmeldung fehlgeschlagen");
        } catch (parseError) {
          if (parseError instanceof Error && parseError.message !== "Anmeldung fehlgeschlagen") {
            throw parseError;
          }
          // Wenn der Fehler nicht als JSON geparst werden kann
          throw new Error("Ungültige Anmeldedaten oder Server nicht erreichbar");
        }
      }
      
      const userData = await res.json();
      
      // Setze Sentry User-Kontext
      setSentryUser(userData);
      
      // Speichere Benutzer im localStorage als Fallback für initialData
      try {
        localStorage.setItem('speiseguru_user', JSON.stringify(userData));
      } catch (e) {
        console.error("Fehler beim Speichern des Benutzers in localStorage:", e);
      }
      
      return userData;
    },
    onSuccess: (user: User) => {
      queryClient.setQueryData(["/api/auth/me"], user);
      
      toast({
        title: "Erfolgreich angemeldet",
        description: `Willkommen zurück, ${user.email.split('@')[0]}!`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Anmeldung fehlgeschlagen",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Registrierungs-Mutation
  const registerMutation = useMutation({
    mutationFn: async (userData: RegisterData) => {
      const res = await apiRequest("POST", "/api/auth/register", userData);
      
      if (!res.ok) {
        try {
          const data = await res.json();
          throw new Error(data.message || "Registrierung fehlgeschlagen");
        } catch (parseError) {
          // Wenn der Fehler nicht als JSON geparst werden kann
          throw new Error("Die Registrierung konnte nicht abgeschlossen werden. Bitte versuchen Sie es später erneut.");
        }
      }
      return await res.json();
    },
    onSuccess: (user: User) => {
      queryClient.setQueryData(["/api/auth/me"], user);
      toast({
        title: "Konto erstellt",
        description: "Sie wurden erfolgreich registriert und angemeldet.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Registrierung fehlgeschlagen",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Logout-Mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/auth/logout");
      
      if (!res.ok) {
        throw new Error("Abmeldung fehlgeschlagen");
      }
      
      // Entferne Sentry User-Kontext
      clearSentryUser();
      
      // Beim Logout ALLE gespeicherten Daten löschen
      try {
        localStorage.removeItem('speiseguru_user');
        localStorage.removeItem('speiseguru_user_id');
        localStorage.removeItem('speiseguru_last_activity');
        // KEIN speiseguru_credentials mehr – wird gar nicht mehr gespeichert
      } catch (e) {
        console.error("Fehler beim Löschen der gespeicherten Daten:", e);
      }
    },
    onSuccess: () => {
      // Auth-State sofort auf null setzen
      queryClient.setQueryData(["/api/auth/me"], null);
      
      // ALLE cached Queries invalidieren und löschen (Favoriten, Restaurantdaten, etc.)
      queryClient.removeQueries({ queryKey: ['/api/favorites'] });
      queryClient.removeQueries({ queryKey: ['/api/users/favorites'] });
      queryClient.removeQueries({ queryKey: ['local-favorites'] });
      queryClient.invalidateQueries();
      
      toast({
        title: "Erfolgreich abgemeldet",
        description: "Sie wurden sicher abgemeldet.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Abmeldung fehlgeschlagen",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Stellen wir sicher, dass user ein definierter Wert (User oder null) ist
  const safeUser: User | null = user === undefined ? null : user;
  
  // Automatisches Abmelden nach Inaktivität (30 Minuten) - vereinfachte Version ohne direkte Abhängigkeit
  useEffect(() => {
    // Nur aktiv, wenn ein Benutzer angemeldet ist
    if (!safeUser) return;
    
    // Benutzeraktivität verfolgen
    const updateActivity = () => {
      localStorage.setItem('speiseguru_last_activity', Date.now().toString());
    };
    
    // Aktivität bei Benutzerinteraktionen aktualisieren
    window.addEventListener('click', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('scroll', updateActivity);
    
    // Inaktivitätsprüfung alle 5 Minuten
    const inactivityCheck = setInterval(() => {
      const lastActivity = localStorage.getItem('speiseguru_last_activity');
      if (!lastActivity) {
        localStorage.setItem('speiseguru_last_activity', Date.now().toString());
        return;
      }
      
      const lastActivityTime = parseInt(lastActivity);
      const inactiveTime = Date.now() - lastActivityTime;
      
      // Automatisches Abmelden nach 30 Minuten Inaktivität
      if (inactiveTime > 30 * 60 * 1000) {
        // Lokale Daten löschen
        localStorage.removeItem('speiseguru_user');
        localStorage.removeItem('speiseguru_user_id');
        
        // Cache komplett zurücksetzen
        queryClient.setQueryData(["/api/auth/me"], null);
        queryClient.removeQueries({ queryKey: ['/api/favorites'] });
        queryClient.removeQueries({ queryKey: ['/api/users/favorites'] });
        queryClient.invalidateQueries();
        
        // Benachrichtigung anzeigen
        toast({
          title: "Automatische Abmeldung",
          description: "Sie wurden aufgrund von Inaktivität automatisch abgemeldet.",
        });
        
        // Optional: Zur Login-Seite weiterleiten
        window.location.href = '/auth';
      }
    }, 5 * 60 * 1000); // Alle 5 Minuten prüfen
    
    // Event-Listener und Timer bereinigen
    return () => {
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('scroll', updateActivity);
      clearInterval(inactivityCheck);
    };
  }, [safeUser, toast]);
  
  return (
    <AuthContext.Provider
      value={{
        user: safeUser,
        isLoading,
        error,
        loginMutation,
        logoutMutation,
        registerMutation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Hook für den Zugriff auf den Auth-Kontext
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth muss innerhalb eines AuthProvider verwendet werden");
  }
  return context;
}